/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from 'vue';
import Router from 'vue-router';
import store from './store/store';
import Helpers from './helpers/methods/helpers';
import api from './helpers/methods/resources';

Vue.use(Router);

var pageRefreshTime = null;

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior() {
        return {
            x: 0,
            y: 0,
        };
    },
    routes: [
        {
            // =============================================================================
            // MAIN LAYOUT ROUTES
            // =============================================================================
            path: '',
            component: () => import('./layouts/main/Main.vue'),
            meta: {
                requiresAuth: true,
            },
            children: [
                // =============================================================================
                // Theme Routes
                // =============================================================================
                {
                    path: '/',
                    name: 'home',
                    props: true,
                    component: () => import('./views/Home.vue'),
                    meta: {
                        title: 'menu.dashboard',
                    },
                    beforeEnter: async (to, from, next) => {
                        try {
                            if (from.name === 'pageLogin' || from.name === 'pageRegister' || from.name === 'pageCode' || from.name === 'pageChangeData') {
                                const appLoading = document.getElementById('loading-bg');
                                appLoading.style.display = 'block';

                                await store.dispatch('getInitData');

                                //UPDATE LANGUAGE
                                if (store.state.userData.AppActiveUser.idLanguage) {
                                    Helpers.updateLocale(store.state.userData.AppActiveUser.idLanguage, true);
                                } else {
                                    Helpers.updateLocale('cs-CZ', false);
                                }

                                //CHECK CONFIRMED
                                if (!api.getUserInfo().registrationDate && to.params.confirm) {
                                    next({
                                        name: 'pageChangeData',
                                    });
                                }

                                //CHECK IF INSTANT FORM
                                if (!to.params.customAttribute) {
                                    next();
                                } else {
                                    //custom attribute && confirmed
                                    if (api.getUserInfo().registrationDate) {
                                        store.dispatch('updateRestricted');
                                    }
                                    //INSTANT FILL FORM
                                    const clients = store.getters.getActualClients;
                                    let forms = [];
                                    const isFormToSign = to.params.param === 's';
                                    let resData = [];
                                    if (isFormToSign) {
                                        resData = await Promise.all(clients.map((c) => Helpers.getPopulatedFormsByClientId(c.idClient)));
                                    } else {
                                        resData = await Promise.all(clients.map((c) => Helpers.getAllFormsByLocation(c.idClient)));
                                    }
                                    //
                                    resData.forEach((fo) => {
                                        fo.forEach((fb) => {
                                            forms.push(fb);
                                        });
                                    });
                                    // console.log(to.params.customAttribute);
                                    // console.log(forms);

                                    const form = forms.find((f) => f.idForm === to.params.customAttribute);

                                    // Allow filling the form from another location, so in the URL must be patient's location
                                    let client = store.state.userData.AppActiveClients.find((client) => {
                                        return client.idCompany === form.idCompany && client.isValid;
                                    });
                                    let idLocation = client.idLocation;

                                    if (form.formType === 'DOCUMENT' || isFormToSign) {
                                        idLocation = clients.filter((c) => c.idCompany === form.idCompany)[0].idLocation;
                                    }

                                    // console.log({
                                    //     idLocation,
                                    //     isFormToSign,
                                    //     form,
                                    // });

                                    next({
                                        name: 'companiesc',
                                        params: {
                                            id: idLocation,
                                            isFormToSign,
                                            form,
                                        },
                                    });
                                }
                            } else next();
                        } catch (error) {
                            console.error(error);
                            next({
                                name: 'pageLogin',
                                params: {
                                    error: 's',
                                },
                            });
                        }
                        //FROM LOGIN/REGISTER PAGE
                    },
                },
                {
                    path: '/news',
                    name: 'news',
                    component: () => import('./views/News.vue'),
                    props: true,
                    meta: {
                        title: 'News',
                    },
                },
                {
                    path: '/profile',
                    name: 'profile',
                    component: () => import('./views/Profile.vue'),
                    meta: {
                        auth: true,
                        title: 'profile.profile',
                    },
                },
                {
                    path: '/companies/:id/:name?',
                    name: 'companiesc',
                    component: () => import('./views/Companies.vue'),
                    meta: {
                        title: 'menu.clinics',
                    },
                },
                {
                    path: '/files',
                    name: 'files',
                    component: () => import('./views/Files.vue'),
                },
                {
                    path: 'appointments',
                    name: 'appointments',
                    props: true,
                    component: () => import('./views/Appointments.vue'),
                    meta: {
                        title: 'menu.appointments',
                    },
                },
                /*
                {
                    path: 'documents',
                    name: 'documents',
                    component: () => import('./views/Documents.vue'),
                    meta: {
                        title: 'menu.financingDocuments'
                    }
                },*/
                {
                    path: 'calendar',
                    name: 'calendar',
                    component: () => import('./views/Calendar.vue'),
                },
                /*
                {
                    path: '/financing/request',
                    name: 'financing',
                    props: true,
                    component: () => import('./views/financing/Request.vue'),
                    meta: {
                        title: 'menu.financingRequest'
                    }
                },*/
            ],
        },
        // =============================================================================
        // FULL PAGE LAYOUTS
        // =============================================================================

        {
            path: '',
            component: () => import('@/layouts/full-page/FullPage.vue'),
            meta: {
                requiresAuth: false,
            },
            children: [
                {
                    path: '/shared/:id/:isIos?',
                    name: 'sharedPdf',
                    beforeEnter(to, from, next) {
                        const { id, isIos } = to.params;
                        if (isIos) {
                            !helpers.isIpad() &&
                                next({
                                    name: 'pageNotAuthorized',
                                });
                        }
                        if (id) {
                            helpers
                                .getSharedPdf(id, isIos)
                                .then((res) => {
                                    to.params['pdf'] = res;
                                    next();
                                })
                                .catch((err) => {
                                    next({
                                        name: 'pageError404',
                                    });
                                });
                        } else {
                            next({
                                name: 'pageError404',
                            });
                        }
                    },
                    component: () => import('./views/SharedPDF.vue'),
                },
                //Změna údajů
                {
                    path: 'changedata',
                    name: 'pageChangeData',
                    component: () => import('./views/pages/ChangeUserData.vue'),
                    beforeEnter(to, from, next) {
                        const user = api.getUserInfo();
                        if (!user.registrationDate) {
                            to.params['user'] = user;
                            next();
                        } else {
                            next(false);
                        }
                    },
                },
            ],
        },
        {
            path: '',
            component: () => import('@/layouts/full-page/FullPage.vue'),
            meta: {
                guest: true,
            },
            children: [
                // =============================================================================
                // PAGES
                // =============================================================================
                {
                    path: '/login/:username?',
                    name: 'pageLogin',
                    props: true,
                    component: () => import('@/views/pages/Login.vue'),
                },
                {
                    path: '/c/:code',
                    name: 'pageCode',
                    component: () => import('@/views/pages/UserConfirmation.vue'),
                },
                {
                    path: '/register/:code?/:param?',
                    name: 'pageRegister',
                    beforeEnter: (to, from, next) => {
                        const allowedParams = ['s']; //s = toSignForm
                        if (to.params.param && !allowedParams.includes(to.params.param)) {
                            next({ name: 'pageRegister' });
                        }
                        next();
                    },
                    component: () => import('@/views/pages/Register.vue'),
                },
                {
                    path: '/r/:code?/:param?',
                    name: 'pageRegister',
                    beforeEnter: (to, from, next) => {
                        const allowedParams = ['s']; //s = toSignForm
                        if (to.params.param && !allowedParams.includes(to.params.param)) {
                            next({ name: 'pageRegister' });
                        }
                        next();
                    },
                    component: () => import('@/views/pages/Register.vue'),
                },
                {
                    path: '/resetPassword',
                    name: 'pageResetPassword',
                    component: () => import('@/views/pages/ResetPassword.vue'),
                },
                {
                    path: '/forgottenPassword',
                    name: 'pageForgottenPassword',
                    component: () => import('@/views/pages/ForgottenPassword.vue'),
                },
                {
                    path: '/f/:code',
                    name: 'pageResetPasswordPhone',
                    component: () => import('@/views/pages/ResetPasswordPhone.vue'),
                },
                {
                    path: '/error-404',
                    name: 'pageError404',
                    component: () => import('@/views/pages/Error404.vue'),
                },
                {
                    path: '/not-authorized',
                    name: 'pageNotAuthorized',
                    component: () => import('@/views/pages/NotAuthorized.vue'),
                },
                {
                    // path: '/form/:id/:isIos?',
                    path: '/form/:id',
                    name: 'formPdf',
                    // beforeEnter(to, from, next) {
                    //     const {
                    //         id,
                    //         isIos
                    //     } = to.params;
                    //     if (isIos) {
                    //         !helpers.isIpad() && next({
                    //             name: 'pageNotAuthorized'
                    //         })
                    //     }
                    //     if (id) {
                    //         helpers.getPdf(id, isIos).then(res => {
                    //             to.params['pdf'] = res;
                    //             next()
                    //         }).catch(err => {

                    //             next({
                    //                 name: 'pageError404'
                    //             })
                    //         })
                    //     } else {
                    //         next()
                    //     }
                    // },
                    component: () => import('./views/FormPDF.vue'),
                },
            ],
        },
        {
            path: '*',
            component: () => import('@/views/pages/Error404.vue'),
        },
    ],
});

router.afterEach((to, from) => {
    // Remove initial loading

    const appLoading = document.getElementById('loading-bg');
    if (appLoading) {
        appLoading.style.display = 'none';
    }

    //Tomuv super page reload hack
    if (!pageRefreshTime) {
        pageRefreshTime = new Date();
    } else {
        if (new Date().getTime() - pageRefreshTime.getTime() > 15 * 60 * 1000) {
            pageRefreshTime = new Date();
            location.reload();
        }
    }
});

import HelperMethods from './helpers/methods/index';
import helpers from './helpers/methods/helpers';

router.beforeResolve((to, from, next) => {
    document.querySelectorAll('.con-vs-popup, .con-vs-dialog').forEach((p) => {
        p.remove();
    });

    // document.querySelector('.vs-dropdown-menu') ? document.querySelector('.vs-dropdown-menu').style.display = 'none': null;
    if (to.matched.some((record) => record.meta.requiresAuth)) {
        // if (from.name !== "pageLogin" && from.name !== "pageRegister" && to.name !== 'pageLogin' && to.name !== 'pageRegister' && localStorage.getItem("token")) { }
        if (!localStorage.getItem('token')) {
            next({
                path: '/login',
            });
        } else {
            next();
        }

        if (store.state.smsFormRestriction) {
            if (to.name !== 'companiesc') {
                next({
                    name: 'pageLogin',
                });
            }
        }
    } else {
        next();
    }
});

export default router;
